<template>
  <div
    class="checkbox-input"
    :class="[
      { [$class('loading')]: loading },
    ]"
  >
    <input
      :id="id"
      v-model="localModelValue"
      class="checkbox-input__input visually-hidden"
      :class="{ [$class('disabled')]: disabled }"
      :disabled="disabled || loading"
      :name="name"
      :required="required ? required : null"
      type="checkbox"
      @change="updateVModel(localModelValue)"
    >

    <label
      class="checkbox-input__label"
      :class="localClassLabel"
      :for="id"
    >
      <span
        class="checkbox-input__label-text"
        v-html="label"
      />

      <icon-tick />
    </label>
  </div>
</template>

<script>

/**
 * Vue: Checkbox input (checkbox-input)
 * -----------------------------------------------------------------------------
 * Checkbox input utility component.
 *
 * @param {String} [classLabel] - Text class for label.
 * @param {Boolean} [disabled] - Input is disabled.
 * @param {String} id - ID for input.
 * @param {String} label - Input label.
 * @param {Boolean} [loading] - Loading state.
 * @param {Boolean} modelValue - Current value provided by v-model.
 * @param {String} [name] - Input name attribute.
 * @param {Boolean} [required] - Input is required.
 *
 * @emits update:model-value - Emit v-model update event.
 *
 */
import IconTick from '~icons/general/tick.svg'

export default {
  name: 'CheckboxInput',

  components: {
    IconTick,
  },

  props: {
    classLabel: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: '',
      required: true,
    },
    label: {
      type: String,
      default: '',
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: Boolean,
      default: false,
      required: true,
    },
    name: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'update:model-value': () => true,
  },

  data() {
    return {
      localModelValue: false,
    }
  },

  computed: {

    /**
     * Compute label text class.
     * @returns {String}
     */
    localClassLabel() {
      return this.classLabel ? this.classLabel : 'text-body-m-regular'
    },
  },

  watch: {

    /**
     * Watch v-model value to update local value.
     */
    modelValue() {
      this.localModelValue = this.modelValue
    },
  },

  mounted() {
    this.localModelValue = this.modelValue
  },

  methods: {

    /**
     * Emit update v-model event.
     * @param {Boolean} value - Updated value.
     */
    updateVModel(value) {
      this.$emit('update:model-value', value)
    },
  },
}
</script>

<style lang="scss">
@import './checkbox-input';
</style>
